import { QueryClientConfig } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import {
    CreateAiSmartReplyPlanItemRequest,
    CreateAiSmartReplyPlanRequest,
} from './api/ai/types/Ai';
import { CreateAccountRequest } from './api/createAccount/types/createAccountRequest';
import { WebPushSendRequest } from './api/webPush/types/webPush';
import { CalendarEventsFilterRanges } from './enums/CalendarEventsFilterRanges';
import { CalendarView } from './enums/CalendarView';
import { ChannelItemType } from './enums/ChannelItemType';
import { ChannelSort } from './enums/ChannelSort';
import { ChannelType } from './enums/ChannelType';
import { ClientBusinessLine } from './enums/ClientBusinessLine';
import { ContactsListSortDirection } from './enums/ContactsListSortDirection';
import { FieldValidationType } from './enums/FieldValidationType';
import { ArticleSort } from './types/Article';
import { CalendarEventDateRangeListFilterItem } from './types/CalendarEventDateRangeListFilterItem';
import { CalendarUserFilterItem } from './types/CalendarUserFilter';
import { ChannelListFilterItem } from './types/ChannelListFilterItem';
import { ContactListSortItem } from './types/ContactListSortItem';
import { EditUserTwilioPhoneData } from './types/EditUserTwilioPhoneData';
import { EmailSettings } from './types/EmailSettings';
import {
    DateRange,
    InboxFiledSortType,
    InboxFilter,
    InboxFilterDirection,
    InboxFilterOptions,
    OwnershipScope,
    SortDirection,
} from './types/InboxTypes';
import { ListType } from './types/Lists';
import { OwnershipScopeInboxFilterItem } from './types/OwnershipScopeInboxFilterItem';
import { QuickCreateContact } from './types/QuickCreateContact';
import { SortInboxFilterItem } from './types/SortInboxFilterItem';
import { DateRangeType } from './types/SortListFilterItem';
import { UserConfig } from './types/UserConfig';
import { UserRoles } from './types/UserRoles';
import { ViewStyle } from './types/View';

//~ User Constants
export const EMPTY_CLIENT_ID = -1;

//~ Conversation Grid Constants
export const GRID_DEFAULT_PAGE_SIZE = 25;

//~ Client Field Constants
export const CLIENT_FIELDS_DEFAULT_LABELS = [
    'address',
    'email',
    'phone',
    'first name',
    'last name',
    'language',
    'registered date',
    'tms code',
];

//~ Contact Constants
export const CONTACTS_FIELD_EMPTY_ROWID = -3;
export const CONTACTS_DEFAULT_PAGE = 0;
export const CONTACTS_DEFAULT_SORT: ContactListSortItem = {
    fieldId: 9,
    direction: ContactsListSortDirection.descending,
};
export const CONTACTS_SEARCH_DEFAULT = '';
export const CONTACTS_SORTABLE_FIELDS = [
    FieldValidationType.email,
    FieldValidationType.address,
    FieldValidationType.date,
    FieldValidationType.dollars,
    FieldValidationType.other,
    FieldValidationType.years,
]; //~ first name, last name must be included in sortable fields, though; do not have a FieldValidtionType

//~ View constants
export const DEFAULT_VIEW_ID = -1;
export const DEFAULT_VIEW_STYLE = ViewStyle.list;

//~ Lists contacts
export const DEFAULT_LIST_ID = -1;
export const DEFAULT_LIST_TYPE = ListType.undefined;

//~ Date constants
export const INBOX_FILTERABLE_DATE_RANGE = Object.values(DateRange).filter(
    x => !isNaN(+x),
) as DateRange[];

export const INBOX_DATE_RANGE_LABELS = {
    0: 'All',
    1: 'Today',
    2: 'Yesterday',
    3: 'Last 3 days',
    4: 'Last 7 days',
    5: 'Last 14 days',
    6: 'Last 30 days',
    7: 'Last 45 days',
    8: 'Last 60 days',
    9: 'Last 90 days',
    10: 'Older than 90 days',
    11: 'Older than 180 days',
};
export const INBOX_DATE_RANGE_TYPES: Array<DateRangeType> = [
    {
        label: 'Activity Date',
        value: 'activityRange',
        isActive: false,
    },
    {
        label: 'Registration Date',
        value: 'registrationRange',
        isActive: false,
    },
    {
        label: 'Channel Date',
        value: 'channelsRange',
        isActive: false,
    },
];

//~ Channel constants
export const CHANNEL_DEFAULT_PAGE = 0;
export const CHANNEL_DEFAULT_PAGE_SIZE = 15;
export const CHANNEL_DEFAULT_SORT = ChannelSort.descending;
export const CHANNEL_TYPE_LABELS = {
    0: 'System',
    1: 'Web Events',
    2: 'Action Plans',
    3: 'ePlans',
    4: 'Listing Events',
    5: 'Calls' /* twilio event: call, message or voicemail */,
    6: 'Facebook Events',
    7: 'Email Events',
    8: 'Text Messages',
    9: 'Form Requests',
    10: 'External Requests',
    11: 'WebPush Events',
};

export const CHANNEL_DEFAULT_FILTERS: Array<ChannelListFilterItem> = [
    {
        label: 'System',
        value: ChannelType.system,
        isActive: true,
    },
    {
        label: 'Web Events',
        value: ChannelType.webEvents,
        isActive: true,
    },
    {
        label: 'Action Plans',
        value: ChannelType.actionPlans,
        isActive: true,
    },
    {
        label: 'ePlans',
        value: ChannelType.ePlans,
        isActive: true,
    },
    {
        label: 'Listing Events',
        value: ChannelType.listingEvents,
        isActive: true,
    },
    {
        label: 'Calls',
        value: ChannelType.callEvents,
        isActive: true,
    },
    {
        label: 'Facebook Events',
        value: ChannelType.facebookEvents,
        isActive: true,
    },
    {
        label: 'Gmail Events',
        value: ChannelType.gmailEvents,
        isActive: true,
    },
    {
        label: 'Text Message Events',
        value: ChannelType.messageEvents,
        isActive: true,
    },
    {
        label: 'Form Requests',
        value: ChannelType.formRequests,
        isActive: true,
    },
    {
        label: 'External Requests',
        value: ChannelType.externalRequests,
        isActive: true,
    },
];

//~ Inbox constants
export const INBOX_DEFAULT_PAGE = 0;
export const INBOX_DEFAULT_PAGE_SIZE = 25;
export const INBOX_DEFAULT_FILTER_CHANNELS: Array<ChannelListFilterItem> = [
    {
        label: 'System',
        value: ChannelType.system,
        isActive: false,
    },
    {
        label: 'Web Events',
        value: ChannelType.webEvents,
        isActive: false,
    },
    {
        label: 'Action Plans',
        value: ChannelType.actionPlans,
        isActive: false,
    },
    {
        label: 'ePlans',
        value: ChannelType.ePlans,
        isActive: false,
    },
    {
        label: 'Listing Events',
        value: ChannelType.listingEvents,
        isActive: false,
    },
    {
        label: 'Calls',
        value: ChannelType.callEvents,
        isActive: false,
    },
    {
        label: 'Facebook Events',
        value: ChannelType.facebookEvents,
        isActive: false,
    },
    {
        label: 'Email Events',
        value: ChannelType.gmailEvents,
        isActive: false,
    },
    {
        label: 'Text Message Events',
        value: ChannelType.messageEvents,
        isActive: false,
    },
];

export const INBOX_DEFAULT_SORT: Array<SortInboxFilterItem> = [
    {
        label: 'Activity Oldest First',
        sortItem: {
            field: InboxFiledSortType.ActivityDate,
            direction: SortDirection.Ascending,
        },
    },
    {
        label: 'Activity Newest First',
        sortItem: {
            field: InboxFiledSortType.ActivityDate,
            direction: SortDirection.Descending,
        },
    },
    {
        label: 'Channels Oldest First',
        sortItem: {
            field: InboxFiledSortType.ChannelDate,
            direction: SortDirection.Ascending,
        },
    },
    {
        label: 'Channels Newest First',
        sortItem: {
            field: InboxFiledSortType.ChannelDate,
            direction: SortDirection.Descending,
        },
    },
    {
        label: 'Registration Oldest First',
        sortItem: {
            field: InboxFiledSortType.RegistrationDate,
            direction: SortDirection.Ascending,
        },
    },
    {
        label: 'Registration Newest First',
        sortItem: {
            field: InboxFiledSortType.RegistrationDate,
            direction: SortDirection.Descending,
        },
    },
    {
        label: 'First Name A-Z',
        sortItem: {
            field: InboxFiledSortType.FirstName,
            direction: SortDirection.Ascending,
        },
    },
    {
        label: 'First Name Z-A',
        sortItem: {
            field: InboxFiledSortType.FirstName,
            direction: SortDirection.Descending,
        },
    },
    {
        label: 'Last Name A-Z',
        sortItem: {
            field: InboxFiledSortType.LastName,
            direction: SortDirection.Ascending,
        },
    },
    {
        label: 'Last Name Z-A',
        sortItem: {
            field: InboxFiledSortType.LastName,
            direction: SortDirection.Descending,
        },
    },
];

export const INBOX_FILTERABLE_CHANNEL_TYPES = Object.values(ChannelType).filter(
    x =>
        !isNaN(+x) &&
        x !== ChannelType.facebookEvents &&
        x !== ChannelType.listingEvents &&
        x !== ChannelType.ePlans &&
        x !== ChannelType.system &&
        x !== ChannelType.actionPlans &&
        x !== ChannelType.formRequests &&
        x !== ChannelType.externalRequests,
) as ChannelType[];

export const INBOX_FILTER_OPTIONS_INITIAL_STATE: InboxFilterOptions = {
    search: '',
    channels: INBOX_FILTERABLE_CHANNEL_TYPES,
    direction: InboxFilterDirection.none,
    channelsRange: DateRange.None,
    registrationRange: DateRange.None,
    activityRange: DateRange.None,
    ownershipScope: OwnershipScope.Follower,
    sort: {
        field: InboxFiledSortType.ActivityDate,
        direction: SortDirection.Descending,
    },
};

export const INBOX_FILTER_INITIAL_STATE: InboxFilter = {
    id: null,
    name: '',
    isDefault: false,
    filter: {
        search: '',
        channels: INBOX_FILTERABLE_CHANNEL_TYPES,
        direction: InboxFilterDirection.none,
        channelsRange: DateRange.None,
        registrationRange: DateRange.None,
        activityRange: DateRange.None,
        ownershipScope: OwnershipScope.Follower,
        sort: {
            field: InboxFiledSortType.ChannelDate,
            direction: SortDirection.Descending,
        },
    },
};

export const INBOX_OWNERSHIP_FILTERS_ADMIN: OwnershipScope[] = [
    OwnershipScope.All,
    OwnershipScope.Follower,
    OwnershipScope.Owner,
];

export const INBOX_OWNERSHIP_FILTERS_NON_ADMIN: OwnershipScope[] = [
    OwnershipScope.Follower,
    OwnershipScope.Owner,
];

export const OWNERSHIP_LABELS = {
    [OwnershipScope.All]: 'All',
    [OwnershipScope.Follower]: "I'm following",
    [OwnershipScope.Owner]: "I'm an owner of",
};

//~ Call List constants
export const EMPTY_CALL_LIST_ID = -1;

//~ Other
export const SYSTEM_PROP_ID = 1000; //~ system fields and views will have an identifier below this value
export const DATE_FORMAT = 'MM/dd/yyyy'; //~ date & date_time formats accepted by backend parser
export const DATE_TIME_FORMAT = 'MM/dd/yyy HH:mm:ss';

//~ Example LISTS
export const LANGUGAES_LIST = ['English', 'French', 'Spanish'];
export const TMS_GROUPS = [
    "Targaryen of King's Landing.",
    'Stark of Winterfell.',
    'Lannister of Casterly Rock.',
    'Arryn of the Eyrie.',
    'Tully of Riverrun.',
    'Greyjoy of Pyke.',
    "Baratheon of Storm's End.",
    'Tyrell of Highgarden.',
];

//~ Calendar Events constants
export const CALENDAR_EVENTS_DATE_RANGE_FILTERS: Array<CalendarEventDateRangeListFilterItem> = [
    {
        label: 'All',
        value: CalendarEventsFilterRanges.all,
    },
    {
        label: 'Today',
        value: CalendarEventsFilterRanges.today,
    },
    {
        label: 'Next 7 Days',
        value: CalendarEventsFilterRanges.next7days,
    },
    {
        label: 'Next 30 Days',
        value: CalendarEventsFilterRanges.next30days,
    },
    {
        label: 'All Future',
        value: CalendarEventsFilterRanges.allfuture,
    },
    {
        label: 'Past 7 Days',
        value: CalendarEventsFilterRanges.past7days,
    },
    {
        label: 'Past 30 Days',
        value: CalendarEventsFilterRanges.past30days,
    },
    {
        label: 'All Past',
        value: CalendarEventsFilterRanges.allpast,
    },
];

export const CALENDAR_DEFAULT_VIEW = CalendarView.timeGridWeek;

export const CALENDAR_DEFAULT_USER_FILTER: CalendarUserFilterItem = {
    label: 'All',
    userName: 'USERS_FILTER_ALL',
};

//~ Form defaults
export const DEFAULT_COMMON_FORM_NUMBER = NaN;

//matched types are auto-selected by the chat bar for quick communication,
//based on the field of the last message in the timeline
export const COMMUNICABLE_TYPES = [
    ChannelItemType.call,
    ChannelItemType.email,
    ChannelItemType.message,
];

export const OWNERSHIPSCOPE_TYPE_LABELS = {
    0: 'All',
    1: "I'm an owner of",
    2: "I'm following",
};

export const INBOX_DEFAULT_OWNERSHIP: Array<OwnershipScopeInboxFilterItem> = [
    {
        label: 'All',
        scope: OwnershipScope.All,
    },
    {
        label: "I'm an owner of",
        scope: OwnershipScope.Owner,
    },
    {
        label: "I'm following",
        scope: OwnershipScope.Follower,
    },
];

export const QUERY_CLIENT_DEFAULTS = {
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: (tries, error) => {
                //small logic to not retry 400
                //(bad requests are intended for validation and shouldnt be retried x times)
                if (axios.isAxiosError(error)) {
                    const axiosError = error as AxiosError;
                    if (
                        axiosError.response?.status === 400 ||
                        axiosError.response?.status === 401
                    ) {
                        return false;
                    }
                }
                return tries < 4; //5 retries, first starting at 0
            },
            cacheTime: 1000 * 60 * 60 * 24 * 7, // 7 days
            gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days
            // identical query subscribers only need to refresh at most, every 5 sec
            // this has no affect on invalidation refreshes
            staleTime: 30000,
        },
    },
} as QueryClientConfig;

export const USER_CONFIG_INITIAL_STATE: UserConfig = {
    clientId: NaN,
    businessLine: ClientBusinessLine.other,
    clientFirstName: '',
    clientLastName: '',
    clientHasUsersWithDialer: false,
    clientHasSic: false,
    clientHasEngagePlatform: false,
    users: [],
    timeZoneId: '',
    clientType: 0,
    userRoles: [UserRoles.CrmViewer],
    userEmail: undefined,
    userEmailBroken: false,
    userPhone: undefined,
    userName: '',
    userFirstName: '',
    userLastName: '',
    dialerIsEnabled: false,
    dialerPhone: undefined,
    dialerPhoneId: 0,
    googleAccountId: undefined,
    googleCalendarChannelId: undefined,
    googleCalendarId: undefined,
    hasPushNotificationsEnabled: false,
    dialerIsSic: false,
    clientHasWebPush: false,
    dialerIsSetup: false,
    googleSessionExpired: true,
    userEmailId: undefined,
    userColour: undefined,
    inboxFilter: INBOX_FILTER_INITIAL_STATE,
    engageToken: undefined,
    isImpersonated: false,
    chatReplyPromptId: 0,
    transcriptPromptId: 0,
    transcriptSummaryPromptId: 0,
    billingPackage: undefined,
    hasBusinessProfile: false,
    selectedBusinessProfile: undefined,
    hasReferralsPaid: false,
};

export const AI_CLIENT_WHITELIST = [1864, 31326, 42113, 43621, 31876, 43767, 43778];

//what a user is eligible to pick from
export const selectableUserRoles = [
    UserRoles.CrmAdmin,
    UserRoles.CrmUser,
    UserRoles.ManageUsers,
    UserRoles.MyLeads,
    UserRoles.MyLeadsReadOnly,
    UserRoles.MyCampaigns,
    UserRoles.MySites,
    UserRoles.MyListings,
];

export const USER_EMAIL_SETTINGS_DEFAULT: EmailSettings = {
    imapHost: '',
    imapPort: 993,
    imapUseSsl: true,
    smtpHost: '',
    smtpPort: 465,
    smtpUseSsl: true,
    emailUserName: '',
    emailUserPassword: '',
    status: 0,
};

export const webPushMessageFormDefaults: WebPushSendRequest = {
    domainInfo: '',
    header: '',
    body: '',
    icon: '',
    url: '',
    actions: [
        {
            action: '',
            title: '',
            data: '',
        },
        {
            action: '',
            title: '',
            data: '',
        },
    ],
};

export const CREATE_ACCOUNT_FORM_DEFAULTS: CreateAccountRequest = {
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    phone: '',
    password: '',
    verifyPassword: '',
    country: '',
    stateProvince: '',
    zipPostalCode: '',
    agreedToTerms: false,
    offerKey: '',
};

export const quickCreateContactDefaults: QuickCreateContact = {
    firstName: 'Direct',
    lastName: 'Call',
    phoneFieldId: 2, //mobile
    phoneNumber: '',
    consent: false,
    terms: false,
    pPolicy: false,
};

export const editUserTwilioPhoneDefaults: EditUserTwilioPhoneData = {
    number: '',
    forwardEnabled: false,
    forwardDelay: 0,
    forwardNumber: '',
    inboundCallMessage: '',
};

export const aiCreateSmartReplyPlanDefaults: CreateAiSmartReplyPlanRequest = {
    clientId: undefined,
    userName: undefined,
    name: '',
    description: '',
    followUpDelay: 15,
    completionPromptId: 0,
    expiresMins: 0,
};

export const aiCreateSmartReplyPlanItemDefaults: CreateAiSmartReplyPlanItemRequest = {
    planId: 0,
    parentPlanItemId: undefined,
    prompt: '',
    name: '',
    description: '',
    clientId: undefined,
    userName: undefined,
    repeats: false,
    repeatLimit: undefined,
};

export const aiPlanExpirationOptions = [
    {
        id: 0,
        label: 'Never',
    },
    {
        id: 60,
        label: '1 hour',
    },
    {
        id: 120,
        label: '2 hours',
    },
    {
        id: 300,
        label: '5 hours',
    },
    {
        id: 1440,
        label: '1 day',
    },
    {
        id: 10080,
        label: '1 week',
    },
    {
        id: 43200,
        label: '1 month',
    },
];

export const HELP_ARTICLE_SORT: ArticleSort[] = [
    {
        label: 'Ordered',
        value: 'OrderNum',
    },
    {
        label: 'Sort by Popularity',
        value: 'ViewCount',
    },
    {
        label: 'Sort by Newest',
        value: 'DateCreatedNew',
    },
    {
        label: 'Sort by Oldest',
        value: 'DateCreatedOld',
    },
    {
        label: 'Sort by A-Z',
        value: 'TitleA-Z',
    },
    {
        label: 'Sort by Z-A',
        value: 'TitleZ-A',
    },
];
